/**
 * Camada 1c · OS NOMES DAS VARIÁVEIS DE AMBIENTE, num lugar só.
 *
 * POR QUE ISTO É UM ARQUIVO E NÃO UMA LINHA DENTRO DO COMPONENTE, e o motivo foi medido em
 * 05/09/2026: hoje `VITE_CX_API_KNOWLEDGE` e `VITE_CX_API_MESSAGING` estão escritos à mão no
 * `App.tsx` de cada app que liga. Erro de digitação ali NÃO QUEBRA NADA: a variável vem
 * `undefined`, a base fica vazia, a chamada sai para uma URL torta, e a tela diz "não
 * respondeu". O sintoma fala de rede, e a causa é uma letra.
 *
 * COM O NOME EM UM LUGAR SÓ, o erro passa a ser impossível de cometer duas vezes: quem escreve
 * um app novo não digita nome de variável nenhum.
 *
 * O PREFIXO `VITE_` É OBRIGATÓRIO e não é estilo: o Vite só expõe ao navegador o que começa
 * com ele. Uma variável sem o prefixo existe no processo de build e chega ao navegador como
 * `undefined`, que é o mesmo sintoma acima por outra causa.
 */

/**
 * Lê uma base do ambiente do navegador, com o nome vindo de um lugar só.
 *
 * DEVOLVE `undefined` E NÃO LANÇA quando falta: a decisão do que fazer sem a base é de quem
 * monta a capacidade, e não desta função. `fonte-de-ajuda.ts` transforma a ausência em
 * degradação declarada, e o transporte de mensagens a transforma em sino indisponível. Lançar
 * aqui tiraria dos dois a chance de dizer na tela o que está faltando.
 */
function ler(nome: string): string | undefined {
  const valor = (import.meta.env as Record<string, unknown>)[nome];
  // String vazia é tratada como ausente de propósito: `VITE_CX_API_KNOWLEDGE=""` num `.env` é
  // um esquecimento de preencher, e não a declaração de uma base vazia.
  return typeof valor === 'string' && valor.trim() !== '' ? valor.trim() : undefined;
}

/**
 * As bases que as capacidades transversais consomem.
 *
 * LIDAS NA CARGA DO MÓDULO, e não a cada render: o valor não muda durante a vida da página, e
 * relê-lo a cada render faria a dependência de efeito mudar de identidade e disparar busca em
 * laço. É a mesma razão pela qual `credenciais.ts` existe separado.
 */
export const BASES = {
  /** `services/knowledge`. Alimenta a gaveta de busca rápida E a Central de Ajuda. */
  conhecimento: ler('VITE_CX_API_KNOWLEDGE'),

  /** `services/messaging`. O plano `tenant` das conversas, que exige `X-Tenant-Id`. */
  mensagens: ler('VITE_CX_API_MESSAGING'),

  /**
   * `control-plane/admin-api`. O plano `control-plane`, que NÃO manda tenant.
   *
   * ELA É OUTRA BASE, e não um caminho diferente da mesma: o operador do plano de controle
   * atravessa todos os clientes, e o serviço que o atende é outro processo, noutra porta, com
   * outro guarda. Tratá-la como variação da de cima faria o admin mandar `X-Tenant-Id` e
   * tomar 401 para sempre.
   */
  controlPlane: ler('VITE_CX_API_ADMIN'),
} as const;
